import React, { useEffect, useRef, useState } from "react";
import { cn } from "../utils/cn";

interface ReadinessScoreProps {
  score: number;
  shortTermHealthScore?: number;
  liquidReservesScore?: number;
  cashFlowMarginScore?: number;
  lifestyleScore?: number;
  size?: "sm" | "md" | "lg";
  showBreakdown?: boolean;
}

/** Determine the fill color class for a sub-score bar */
function barColorClass(value: number): string {
  if (value >= 70) return "breakdown-bar-fill--good";
  if (value >= 40) return "breakdown-bar-fill--fair";
  return "breakdown-bar-fill--poor";
}

/** Build a concise actionable explanation */
function getActionableExplanation(
  score: number,
  components: { name: string; value: number }[]
): string | null {
  if (score >= 80) return null; // gauge speaks for itself
  const sorted = [...components].sort((a, b) => a.value - b.value);
  if (score >= 50) {
    const lowest = sorted.slice(0, 2).map((c) => c.name);
    return `Focus on: ${lowest.join(", ")}.`;
  }
  return `Take action on: ${sorted[0].name}. See recommendations below.`;
}

export const ReadinessScore: React.FC<ReadinessScoreProps> = React.memo(
  ({
    score,
    shortTermHealthScore,
    liquidReservesScore,
    cashFlowMarginScore,
    lifestyleScore,
    size = "md",
    showBreakdown = false,
  }) => {
    const ringRef = useRef<SVGCircleElement>(null);
    const [animated, setAnimated] = useState(false);

    // Clamp score for display purposes
    const displayScore = Math.max(-100, Math.min(200, score));
    const clampedScore = Math.max(0, Math.min(100, score));

    const getScoreColor = (s: number): string => {
      if (s >= 80) return "text-success-600";
      if (s >= 50) return "text-warning-600";
      return "text-error-600";
    };

    const getStrokeColor = (s: number): string => {
      if (s >= 80) return "#10b981";
      if (s >= 50) return "#f59e0b";
      return "#ef4444";
    };

    const getStatusText = (s: number): string => {
      if (s >= 80) return "Well Prepared";
      if (s >= 50) return "Needs Attention";
      return "Action Required";
    };

    const getProgressRingClass = (s: number): string => {
      if (s >= 80) return "stroke-success-500";
      if (s >= 50) return "stroke-warning-500";
      return "stroke-error-500";
    };

    const sizes = {
      sm: {
        svg: 120,
        stroke: 12,
        fontSize: "score-value-sm",
        statusSize: "score-status-sm",
      },
      md: {
        svg: 180,
        stroke: 18,
        fontSize: "score-value-md",
        statusSize: "score-status-md",
      },
      lg: {
        svg: 240,
        stroke: 24,
        fontSize: "score-value-lg",
        statusSize: "score-status-lg",
      },
    };

    const config = sizes[size];
    const radius = (config.svg - config.stroke) / 2;
    const circumference = radius * 2 * Math.PI;
    const offset = circumference - (clampedScore / 100) * circumference;

    // Animate ring from 0 to target on mount
    useEffect(() => {
      // Check prefers-reduced-motion
      const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
      // Use rAF in both branches so setState is never synchronous in the effect body
      const raf = requestAnimationFrame(() => setAnimated(true));
      if (mq.matches) {
        // Reduced motion — animation runs instantly via CSS (transition: none)
        return () => cancelAnimationFrame(raf);
      }
      // Normal — the rAF delay lets the initial (empty) ring paint first
      return () => cancelAnimationFrame(raf);
    }, []);

    // Build breakdown data for bars
    const breakdownItems =
      showBreakdown &&
      shortTermHealthScore !== undefined &&
      liquidReservesScore !== undefined &&
      cashFlowMarginScore !== undefined &&
      lifestyleScore !== undefined
        ? [
            { name: "Short-Term Health", value: shortTermHealthScore },
            { name: "Emergency Fund", value: liquidReservesScore },
            { name: "Cash Flow Margin", value: cashFlowMarginScore },
            { name: "Lifestyle Score", value: lifestyleScore },
          ]
        : null;

    const explanation = breakdownItems
      ? getActionableExplanation(score, breakdownItems)
      : null;

    return (
      <div
        className="readiness-score-container"
        id="readiness-score"
        role="img"
        aria-label={`Retirement readiness score: ${displayScore}%`}
      >
        <div
          className="score-gauge"
          style={{ width: config.svg, height: config.svg }}
        >
          <svg
            width={config.svg}
            height={config.svg}
            className="score-text-rotated"
          >
            {/* Background ring — gray-200 for better contrast */}
            <circle
              cx={config.svg / 2}
              cy={config.svg / 2}
              r={radius}
              stroke="#e5e7eb"
              strokeWidth={config.stroke}
              fill="none"
            />
            {/* Progress ring with CSS transition animation */}
            <circle
              ref={ringRef}
              cx={config.svg / 2}
              cy={config.svg / 2}
              r={radius}
              stroke={getStrokeColor(score)}
              strokeWidth={config.stroke}
              fill="none"
              strokeDasharray={circumference}
              strokeDashoffset={animated ? offset : circumference}
              strokeLinecap="round"
              className={cn(
                "score-ring",
                getProgressRingClass(score),
                !animated && "score-ring--initial"
              )}
            />
          </svg>

          <div className="score-center">
            <div
              className={cn(
                "score-value",
                config.fontSize,
                getScoreColor(score)
              )}
            >
              <span>{displayScore}</span>
              <span className="score-percent-symbol">%</span>
            </div>
            <span className={cn("score-status", config.statusSize)}>
              {getStatusText(score)}
            </span>
          </div>
        </div>

        <div className="score-label-section">
          <span className="score-readiness-label">Readiness</span>

          {/* Actionable explanation (only for sub-par scores) */}
          {explanation && (
            <span className="score-explanation">{explanation}</span>
          )}

          {/* Breakdown as mini progress bars */}
          {breakdownItems && (
            <div className="score-breakdown">
              {breakdownItems.map((item, idx) => (
                <div key={item.name} className="breakdown-row">
                  <span className="breakdown-name">{item.name}</span>
                  <div className="breakdown-bar-track">
                    <div
                      className={cn(
                        "breakdown-bar-fill",
                        barColorClass(item.value)
                      )}
                      style={{
                        width: animated
                          ? `${Math.min(item.value, 100)}%`
                          : "0%",
                        transitionDelay: `${idx * 150}ms`,
                      }}
                    />
                  </div>
                  <span className="breakdown-value">{item.value}%</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }
);

ReadinessScore.displayName = "ReadinessScore";
